<template>
  <div class="employee" :class="{ 'employee--has-background': hasBackground }">
    <AEmployeeSelect
      ref="employeeSelectRef"
      v-model="employeeDataLocal"
      :custom-filter="customFilter"
      :required="required"
      @clear-employee="handleClearEmployee"
    />

    <!-- Timeblocks Timeline -->
    <div v-if="selectedEmployee" class="employee__timeblocks">
      <AEmployeeTimeblock
        v-for="(timeblock, index) in timeblocks"
        :key="timeblock._clientId || timeblock.id || `timeblock-${index}`"
        :ref="(el) => setTimeblockRef(el, index)"
        :model-value="timeblock"
        :employee="selectedEmployee"
        :all-employee-timeblocks="timeblocks"
        :all-timeblocks="allTimeblocksForCurrentEmployee"
        :deleted-timeblock-ids="deletedTimeblockIds"
        :draft-timeblocks="draftTimeblocks"
        :current-index="index"
        :deletable="timeblocks.length > 1"
        :index="index"
        @update:model-value="(val) => handleUpdateTimeblock(index, val)"
        @add-timeblock="handleAddTimeblock(index)"
        @remove-timeblock="handleRemoveTimeblock(index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onMounted } from 'vue';
import { useStore } from 'vuex';
import AEmployeeSelect from './a-employee-select.vue';
import AEmployeeTimeblock from './a-employee-timeblock.vue';
import type {
  EmployeeData,
  TimeblockData,
  CustomFilterFunction,
} from './types';

interface Props {
  employeeData: EmployeeData;
  timeblocks?: TimeblockData[];
  allTimeblocks?: TimeblockData[]; // All timeblocks from all employees
  deletedTimeblockIds?: number[]; // IDs of saved timeblocks that have been deleted
  draftTimeblocks?: TimeblockData[]; // Draft timeblocks from all employees in draft subtask
  customFilter?: CustomFilterFunction;
  hasBackground?: boolean;
  required?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  timeblocks: () => [],
  allTimeblocks: () => [],
  deletedTimeblockIds: () => [],
  draftTimeblocks: () => [],
  customFilter: undefined,
  hasBackground: true,
  required: false,
});

const emit = defineEmits<{
  (e: 'update:employeeData', value: EmployeeData): void;
  (e: 'update:timeblocks', value: TimeblockData[]): void;
  (e: 'clearEmployee'): void;
  (e: 'addTimeblock', afterIndex: number): void;
}>();

const store = useStore();

// Local models with v-model sync
const employeeDataLocal = computed({
  get: () => props.employeeData,
  set: (value: EmployeeData) => emit('update:employeeData', value),
});

// Refs for validation
const employeeSelectRef = ref<InstanceType<typeof AEmployeeSelect> | null>(
  null,
);
const timeblockRefs = ref<any[]>([]);

const setTimeblockRef = (el: any, index: number) => {
  if (el) {
    timeblockRefs.value[index] = el;
  }
};

// Get the full employee object from store
const selectedEmployee = computed(() => {
  if (!props.employeeData.id) return null;
  const employees = store.getters['tm/enrichedEmployees'];
  return employees.find((emp: any) => emp.id == props.employeeData.id) || null;
});

// Filter allTimeblocks to only include timeblocks for current employee
const allTimeblocksForCurrentEmployee = computed(() => {
  if (!props.employeeData.id) return [];
  return props.allTimeblocks.filter(
    (tb) => tb.employee_id == props.employeeData.id,
  );
});

// Watch for when employee is selected and ensure at least one timeblock exists
watch(
  () => props.employeeData.id,
  (newId, oldId) => {
    // If employee just got selected (id changed from null/undefined to a value)
    // and there are no timeblocks for this employee, request one to be added
    if (newId && !oldId && props.timeblocks.length === 0) {
      nextTick(() => {
        emit('addTimeblock', -1);
      });
    }
  },
  { flush: 'post' }, // Ensure watch runs after all component updates
);

// On mount, check if employee has ID but no timeblocks
onMounted(() => {
  if (props.employeeData.id && props.timeblocks.length === 0) {
    nextTick(() => {
      emit('addTimeblock', -1);
    });
  }
});

const handleClearEmployee = () => {
  emit('clearEmployee');
};

const handleUpdateTimeblock = (index: number, value: TimeblockData) => {
  const updatedTimeblocks = [...props.timeblocks];
  updatedTimeblocks[index] = value;
  emit('update:timeblocks', updatedTimeblocks);
};

const handleRemoveTimeblock = (index: number) => {
  const updatedTimeblocks = [...props.timeblocks];
  updatedTimeblocks.splice(index, 1);
  emit('update:timeblocks', updatedTimeblocks);
};

const handleAddTimeblock = (afterIndex: number) => {
  emit('addTimeblock', afterIndex);
};

// Expose validate method that validates all child components
const validate = async () => {
  let isValid = true;
  // Validate employee select
  if (employeeSelectRef.value) {
    const selectValid = await employeeSelectRef.value.validate();
    isValid = isValid && selectValid;
  }

  // Validate all timeblocks
  for (const timeblockRef of timeblockRefs.value) {
    if (timeblockRef) {
      const timeblockValid = await timeblockRef.validate();
      isValid = isValid && timeblockValid;
    }
  }

  return isValid;
};

defineExpose({
  validate,
});
</script>

<style lang="sass" scoped>
.employee
  width: 100%
  margin-bottom: 16px

  &--has-background
    border: 1px solid #e5e5e5
    border-top: 3px solid #7f39fb
    padding: 16px
    border-radius: 8px 8px 0 0

  &__timeblocks
    display: flex
    flex-direction: column
    gap: 8px
</style>
